import { useEffect, useState } from "react";
import { useWallet } from "../hooks/useWallet";
import { WalletProviderChooser } from "../components/WalletProviderChooser";
import { useApi } from "../hooks/useApi";
import { ADDRESS_ENV, EXPECTED_CHAIN, EXPECTED_CHAIN_LABEL, envAddress, requireEnvAddress } from "../utils/config";
import { LP_COMMUNITY_VAULT_ABI } from "../utils/contracts";

interface NftRecord {
  tokenId: number;
  active: boolean;
}

interface LpVaultState {
  sourceLpTokenId: bigint;
  uncxLockId: bigint;
  unlockTime: bigint;
  remainingLiquidity: bigint;
  eligibleUnclaimedCount: bigint;
  uncxReleased: boolean;
}

export default function LpClaimPage() {
  const { account, walletClient, publicClient, connect, switchToExpectedChain, chainId, isConnecting, error: walletError, walletOptions, selectedWalletId, selectWallet } = useWallet();
  const vaultConfigured = Boolean(envAddress(ADDRESS_ENV.lpCommunityVault));
  const { get } = useApi();
  const [nfts, setNfts] = useState<NftRecord[]>([]);
  const [claimedByToken, setClaimedByToken] = useState<Record<number, boolean>>({});
  const [forfeitedByToken, setForfeitedByToken] = useState<Record<number, boolean>>({});
  const [state, setState] = useState<LpVaultState | null>(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState<number | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => { void loadVaultState(); }, [publicClient, vaultConfigured]);
  useEffect(() => { if (account) void loadWallet(); }, [account, publicClient, vaultConfigured]);

  async function loadVaultState() {
    if (!publicClient || !vaultConfigured) {
      setState(null);
      return;
    }
    try {
      const address = requireEnvAddress(ADDRESS_ENV.lpCommunityVault);
      const [sourceLpTokenId, uncxLockId, unlockTime, remainingLiquidity, eligibleUnclaimedCount, uncxReleased] = await Promise.all([
        publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "sourceLpTokenId" }),
        publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "uncxLockId" }),
        publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "unlockTime" }),
        publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "remainingLiquidity" }),
        publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "eligibleUnclaimedCount" }),
        publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "uncxReleased" })
      ]);
      setState({ sourceLpTokenId, uncxLockId, unlockTime, remainingLiquidity, eligibleUnclaimedCount, uncxReleased });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not load LP vault state");
    }
  }

  async function loadWallet() {
    if (!account) return;
    setLoading(true);
    setError(null);
    try {
      const data = await get<NftRecord[]>(`/indexer/nfts?owner=${account}`);
      const activeNfts = data.filter((n) => n.active);
      setNfts(activeNfts);
      await loadClaimFlags(activeNfts);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load NFTs");
    } finally {
      setLoading(false);
    }
  }

  async function loadClaimFlags(activeNfts: NftRecord[]) {
    if (!publicClient || !vaultConfigured) return;
    try {
      const address = requireEnvAddress(ADDRESS_ENV.lpCommunityVault);
      const entries = await Promise.all(activeNfts.map(async (nft) => {
        const [claimed, forfeited] = await Promise.all([
          publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "lpClaimed", args: [BigInt(nft.tokenId)] }),
          publicClient.readContract({ address, abi: LP_COMMUNITY_VAULT_ABI, functionName: "lpForfeited", args: [BigInt(nft.tokenId)] })
        ]);
        return [nft.tokenId, { claimed, forfeited }] as const;
      }));
      setClaimedByToken(Object.fromEntries(entries.map(([tokenId, flags]) => [tokenId, flags.claimed])));
      setForfeitedByToken(Object.fromEntries(entries.map(([tokenId, flags]) => [tokenId, flags.forfeited])));
    } catch {
      setClaimedByToken({});
      setForfeitedByToken({});
    }
  }

  async function claimLp(tokenId: number) {
    if (!walletClient || !account) return;
    setClaiming(tokenId);
    setError(null);
    setSuccess(null);
    try {
      const tx = await walletClient.writeContract({
        address: requireEnvAddress(ADDRESS_ENV.lpCommunityVault),
        abi: LP_COMMUNITY_VAULT_ABI,
        functionName: "claimLpPositionFor",
        args: [BigInt(tokenId)],
        account,
        chain: EXPECTED_CHAIN
      });
      setSuccess(`Submitted LP claim for CODE APE #${tokenId}: ${tx}`);
      await loadVaultState();
      await loadWallet();
    } catch (e: unknown) {
      setError(e instanceof Error ? formatClaimError(e.message) : "LP claim failed");
    } finally {
      setClaiming(null);
    }
  }

  async function releaseLock() {
    if (!walletClient || !account) return;
    setReleasing(true);
    setError(null);
    setSuccess(null);
    try {
      const tx = await walletClient.writeContract({
        address: requireEnvAddress(ADDRESS_ENV.lpCommunityVault),
        abi: LP_COMMUNITY_VAULT_ABI,
        functionName: "releaseFromUncx",
        args: [],
        account,
        chain: EXPECTED_CHAIN
      });
      setSuccess(`Submitted UNCX release: ${tx}`);
      await loadVaultState();
    } catch (e: unknown) {
      setError(e instanceof Error ? formatClaimError(e.message) : "UNCX release failed");
    } finally {
      setReleasing(false);
    }
  }

  const isExpectedChain = chainId === EXPECTED_CHAIN.id;
  const nowSeconds = BigInt(Math.floor(Date.now() / 1000));
  const isRegistered = Boolean(state && state.sourceLpTokenId > 0n && state.uncxLockId > 0n);
  const isUnlocked = Boolean(state && state.unlockTime > 0n && nowSeconds >= state.unlockTime);
  const sharePreview = state && state.eligibleUnclaimedCount > 0n
    ? state.eligibleUnclaimedCount === 1n ? state.remainingLiquidity : state.remainingLiquidity / state.eligibleUnclaimedCount
    : 0n;

  return (
    <main className="page">
      <div className="container" style={{ maxWidth: 860, paddingBottom: "var(--space-24)" }}>
        <div style={{ marginBottom: "var(--space-8)" }}>
          <span className="eyebrow eyebrow--gold">// community LP claim</span>
          <h1 className="text-4xl" style={{ marginBottom: "var(--space-3)" }}>
            Claim Your <span className="gradient-text">LP Position</span>
          </h1>
          <p className="text-muted">
            The official v4 LP NFT is locked in UNCX for 90 days under the community vault. After unlock,
            each unburned, unclaimed CODE APE can split a pro-rata v4 LP position to the current NFT owner. Claiming means the owner directly holds that liquidity position.
          </p>
          <p className="lede-alt">
            This is not a team withdrawal page. Anyone can pay gas to trigger the claim, but the LP position always goes to the current owner of that tokenId. Hold the Ape to keep LP optionality; burn it and that unclaimed LP right is gone.
          </p>
        </div>

        {state && (
          <div className="stat-grid" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", marginBottom: "var(--space-6)" }}>
            <Metric label="UNCX lock" value={state.uncxLockId > 0n ? `#${state.uncxLockId}` : "pending finalize"} />
            <Metric label="Unlock" value={state.unlockTime > 0n ? formatUnix(state.unlockTime) : "pending"} />
            <Metric label="Eligible left" value={state.eligibleUnclaimedCount.toLocaleString()} tone="green" />
            <Metric label="Next share" value={sharePreview > 0n ? sharePreview.toString() : "pending"} />
          </div>
        )}

        {!vaultConfigured && (
          <div className="alert alert-error" style={{ marginBottom: "var(--space-6)" }}>
            Contract writes disabled: {ADDRESS_ENV.lpCommunityVault} is not configured.
          </div>
        )}

        {account && !isExpectedChain && (
          <div className="alert alert-warning" style={{ marginBottom: "var(--space-6)", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div>
              <strong>Wrong network</strong>
              <div className="text-sm text-muted">Switch to {EXPECTED_CHAIN_LABEL} before claiming LP.</div>
            </div>
            <button className="btn btn-secondary" onClick={switchToExpectedChain}>Switch to {EXPECTED_CHAIN_LABEL}</button>
          </div>
        )}

        {!isRegistered && vaultConfigured && (
          <div className="alert alert-warning" style={{ marginBottom: "var(--space-6)" }}>
            <span>⏳</span>
            <div>
              <strong>LP claim opens after launch finalization and the 90-day UNCX unlock.</strong>
              <div className="text-sm text-muted">Before finalization, the source LP position and UNCX lock id are not registered yet. After unlock, unburned NFTs can claim their split LP positions.</div>
            </div>
          </div>
        )}

        {isRegistered && !isUnlocked && (
          <div className="alert alert-info" style={{ marginBottom: "var(--space-6)" }}>
            <span>🔒</span>
            <div>
              <strong>UNCX lock is active.</strong>
              <div className="text-sm text-muted">Claiming LP positions starts after {state ? formatUnix(state.unlockTime) : "the unlock time"}.</div>
            </div>
          </div>
        )}

        {isRegistered && isUnlocked && !state?.uncxReleased && account && (
          <div className="card" style={{ marginBottom: "var(--space-6)", display: "flex", justifyContent: "space-between", gap: "var(--space-4)", alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 700 }}>Release the source LP from UNCX</div>
              <div className="text-sm text-muted">This is permissionless. A claim will also try to release first if needed.</div>
            </div>
            <button className="btn btn-secondary" onClick={releaseLock} disabled={!isExpectedChain || releasing}>
              {releasing ? <><span className="spinner" /> Releasing…</> : "Release UNCX Lock"}
            </button>
          </div>
        )}

        {!account && (
          <div className="alert alert-info wallet-connect-panel" style={{ marginBottom: "var(--space-6)" }}>
            <div>
              <strong>Connect your wallet</strong>
              <div className="text-sm text-muted">Open this page inside a mobile wallet browser if you are on a phone.</div>
            </div>
            <div className="wallet-connect-panel__actions">
              <WalletProviderChooser walletOptions={walletOptions} selectedWalletId={selectedWalletId} selectWallet={selectWallet} />
              <button className="btn btn-secondary" onClick={() => void connect()} disabled={isConnecting}>
                {isConnecting ? "Connecting…" : "Connect Wallet"}
              </button>
            </div>
          </div>
        )}

        {walletError && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{walletError}</div>}
        {error && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom: "var(--space-4)", overflowWrap: "anywhere" }}>{success}</div>}

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-12)" }}>
            <span className="spinner spinner-lg" />
          </div>
        )}

        {!loading && nfts.length === 0 && account && (
          <div className="card text-center" style={{ padding: "var(--space-16)" }}>
            <div style={{ fontSize: "3rem", marginBottom: "var(--space-4)" }}>🧩</div>
            <p className="text-xl text-muted">No active CODE APEs found in this wallet.</p>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          {nfts.map((nft) => {
            const claimed = claimedByToken[nft.tokenId] ?? false;
            const forfeited = forfeitedByToken[nft.tokenId] ?? false;
            return (
              <div key={nft.tokenId} className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)", flexWrap: "wrap" }}>
                <div>
                  <div className="text-xl" style={{ fontWeight: 600, marginBottom: 4 }}>CODE APE #{nft.tokenId}</div>
                  <div className="text-sm text-muted">
                    {claimed ? "LP already claimed" : forfeited ? "LP forfeited by burn" : isUnlocked ? "Ready after UNCX release" : "Waiting for 90-day unlock"}
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => claimLp(nft.tokenId)}
                  disabled={!vaultConfigured || !isExpectedChain || !isRegistered || !isUnlocked || claimed || forfeited || claiming === nft.tokenId}
                >
                  {claiming === nft.tokenId ? <><span className="spinner" /> Claiming…</> : "Claim LP Position"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: "green" | "gold" }) {
  const color = tone === "green" ? "var(--color-green)" : tone === "gold" ? "var(--color-gold)" : undefined;
  return (
    <div className="stat-card" style={{ textAlign: "left" }}>
      <div className="stat-value" style={{ color, fontSize: "var(--font-xl)", overflowWrap: "anywhere" }}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function formatUnix(seconds: bigint): string {
  return new Date(Number(seconds) * 1000).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatClaimError(message: string): string {
  if (/LockStillActive|too early|active/i.test(message)) return "The UNCX lock is still active.";
  if (/LpAlreadyClaimed/i.test(message)) return "This token already claimed its LP position.";
  if (/LpAlreadyForfeited/i.test(message)) return "This token forfeited LP rights when it was burned.";
  if (/NoLpClaim/i.test(message)) return "No LP share is available for this token.";
  return message || "LP claim failed.";
}
